import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

import { useTranslation } from "react-i18next";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { useMediaQuery } from "react-responsive";
import { SMALL_SCREEN, x_LARGE_SCREEN } from "../../../assets/Utils/generic";
import { openModal, closeModal } from "../../../assets/Utils/modal";
import { Reviews } from "../Directsearch/Results";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import {
  HotelDetails,
  wsAvailability,
} from "../../../assets/Services/wsSearch";
import { parseParams } from "../../hooks/useSearch";
import { LANG } from "../../../config/Lang";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "../../components/Carousel/Carousel";
import IconGallery from "../../../assets/icons/IconGallery";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/userSlice";
import IconStar from "../../../assets/icons/IconStar";
import imageBadgePlus from "../../../assets/img/directsearch/badge_plus.avif";
import IconPhone from "../../../assets/icons/IconPhone";
import IconChat from "../../../assets/icons/IconChat";
import IconCopy from "../../../assets/icons/IconCopy";
import IconOpen from "../../../assets/icons/IconOpen";

import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
} from "date-fns";

import { advantagesMap } from "../../../config/advantages";
import { useCopyToClipboard } from "../../hooks/hooks";

import { toast } from "react-toastify";
import ImageWithFallback from "../../components/ImageWithFallback/ImageWithFallback";

import { ANALYTICS_DETAILS_SECTION } from "../../../config/analytics";
import NotFound from "../NotFound/NotFound";
import { FullScreenCarousel } from "./FullScreenCarousel";
import { SearchParameters } from "../../../assets/Services/wsSearch";
import {
  getHotelAvailability,
  getHotelDetails,
} from "../../../assets/Utils/search";
import AvailabilityCard from "./AvailabilityCard";
import AvailabilityCardMobile from "./AvailabilityCardMobile";
import { extractDomain } from "../Directsearch/utils";
import HotelDescription from "./HotelDescription";
import DetailMap from "./DetailMap";
import IconInfo from "../../../assets/icons/IconInfo";

const resellableAdvantages = ["resellable_rate", "flexibility"];

const DetailPage = () => {
  const params = useParams();

  const [urlParams] = useSearchParams();

  const firstLoad = useRef<boolean>(false);

  const stickySideRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const md = useMediaQuery({ query: "(max-width: 768px)" });
  const isLargeScreen = useMediaQuery({ query: x_LARGE_SCREEN });
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });

  const [hotelDetail, setHotelDetail] = useState<HotelDetails>();

  const [isLoading, setIsLoading] = useState(true);

  const [availability, setAvailability] = useState<wsAvailability>();

  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);

  const [activeSection, setActiveSection] = useState<string>("info");

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentSlide, setCarouselCurrentSlide] = useState(0);
  const [carouselSlidesCount, setCarouselSlidesCount] = useState(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateCarouselState = () => {
      setCarouselCurrentSlide(carouselApi.selectedScrollSnap());
      setCarouselSlidesCount(carouselApi.scrollSnapList().length);
    };

    updateCarouselState();

    carouselApi.on("select", updateCarouselState);

    return () => {
      carouselApi.off("select", updateCarouselState);
    };
  }, [carouselApi]);

  const [currentLanguageCode, setCurrentLanguageCode] = useState<string>("");

  const [searchParameters, setSearchParameters] = useState<SearchParameters>();

  const [navbarHeight, setNavbarHeight] = useState(0);
  const [stickySideHeight, setStickySideHeight] = useState(0);

  const user = useSelector(selectUser);

  const { i18n, t } = useTranslation();

  const { copyToClipboard } = useCopyToClipboard();

  const highlightedAdvantage = hotelDetail?.advantagesOnDirectWebsite?.[0];

  const HighlightedAdvantageIcon =
    advantagesMap[highlightedAdvantage as keyof typeof advantagesMap]?.icon;
  const highlightedAdvantageLabel =
    advantagesMap[highlightedAdvantage as keyof typeof advantagesMap]?.label;

  const isExtraDiscountApplied =
    !!hotelDetail?.isLoyaltyEnabled &&
    !!hotelDetail?.isLoyaltyEnabledAdmin &&
    !!hotelDetail?.loyatyDiscountPercentage;

  const facilitiesToShow = useMemo(() => {
    try {
      return hotelDetail?.property?.facilities?.slice(0, 10);
    } catch (e) {}
  }, [hotelDetail]);

  const reviewsToShow = useMemo(() => {
    const reviews = hotelDetail?.reviewData?.reviews || [];

    const unique: any[] = Array.from(
      Object.values(
        reviews.reduce((acc: any, review) => {
          const id = review.review_id;

          if (!acc[id]) {
            acc[id] = review;
            return acc;
          }

          const current = acc[id];
          const langs = [i18n.language, "en-gb", "en-us"];

          const currentPriority = langs.indexOf(current.language_code);
          const newPriority = langs.indexOf(review.languagecode);

          if (
            newPriority !== -1 &&
            (currentPriority === -1 || newPriority < currentPriority)
          ) {
            acc[id] = review;
          }

          return acc;
        }, {}),
      ),
    );

    const timestamps = unique.map((r) => new Date(r.date).getTime());
    const minDate = Math.min(...timestamps);
    const maxDate = Math.max(...timestamps);

    const avgRating = hotelDetail?.reviewData?.reviewScore ?? 0;

    const ratingDiffs = unique.map((r) =>
      Math.abs(r.average_score - avgRating),
    );
    const maxDiff = Math.max(...ratingDiffs) || 1;

    return unique
      .map((r) => {
        const time = new Date(r.date).getTime();

        const recencyScore = (time - minDate) / (maxDate - minDate || 1);

        const diff = Math.abs(r.average_score - avgRating);
        const closenessScore = 1 - diff / maxDiff;

        const weightedScore = 0.5 * recencyScore + 0.5 * closenessScore;

        return { ...r, weightedScore };
      })
      .sort((a, b) => b.weightedScore - a.weightedScore)
      .slice(0, 5);
  }, [hotelDetail?.reviewData?.reviews, i18n.language]);

  const advantagesIcons = [IconPhone, IconChat];

  const paramsObject: SearchParameters | undefined = useMemo(() => {
    const array = Array.from(urlParams.entries()).map(
      ([k, v]: [string, string]) => [
        k,
        parseParams(k as keyof SearchParameters, v),
      ],
    );
    if (array.length === 0) return;
    return Object.fromEntries(array) as any;
  }, [urlParams]);

  useEffect(() => {
    setSearchParameters(paramsObject);
  }, [paramsObject]);

  let urlWebsite = hotelDetail?.website;
  if (urlWebsite && !urlWebsite.includes("http"))
    urlWebsite = `https://${urlWebsite}`;

  // Definizione delle sezioni per la navigazione
  const goSections = [
    { name: "Info", ref: "info" },
    { name: "Recensioni", ref: "reviews" },
    { name: "Posizione", ref: "map" },
    // { name: "FAQ", ref: "faq" },
  ];

  useEffect(() => {
    if (!firstLoad.current) {
      fetchHotelAvailability();
    }
  }, [searchParameters, hotelDetail, firstLoad]);

  const fetchHotelAvailability = async () => {
    setIsAvailabilityLoading(true);
    if (!hotelDetail) {
      return;
    }

    if (!searchParameters) {
      return;
    }

    try {
      const res = await getHotelAvailability(
        {
          id: hotelDetail._id,
          priceProviders: hotelDetail.priceProviders
            ? hotelDetail.priceProviders
            : {
                bookingcom: hotelDetail.bookingId
                  ? { id: hotelDetail.bookingId }
                  : undefined,
                hotelscom: hotelDetail.hotelsId
                  ? { id: hotelDetail.hotelsId }
                  : undefined,
              },
        },
        searchParameters,
      );
      setAvailability(res);
      firstLoad.current = true;
    } catch {
      setAvailability(undefined);
    }
    setIsAvailabilityLoading(false);
  };

  useEffect(() => {
    const hotelCode = params.hotel;
    if (hotelCode) {
      fetchHotelDetails(hotelCode, i18n.language);
    }
  }, [params, i18n.language]);

  const fetchHotelDetails = async (id: string, lang: string) => {
    setIsLoading(true);
    try {
      const res = await getHotelDetails(id, lang);
      setHotelDetail(res);
    } catch {
      setHotelDetail(undefined);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const nav = document.getElementById("navbar");
    if (nav) {
      setNavbarHeight(nav.clientHeight);
    }
  }, []);

  useEffect(() => {
    if (stickySideRef.current) {
      setStickySideHeight(stickySideRef.current.clientHeight);
    }
  }, [stickySideRef.current?.clientHeight]);

  // Intersection Observer per rilevare la sezione attiva durante lo scrolling
  useEffect(() => {
    const observerOptions = {
      root: document.getElementById("modal-detail-body"),
      rootMargin: "-100px 0px -60% 0px", // Considera l'elemento visibile quando è nel primo 40% della viewport
      threshold: 0.1,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    // Osserva tutte le sezioni definite in goSections
    goSections.forEach((section) => {
      const element = document.getElementById(section.ref);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [goSections]);

  useEffect(() => {
    const scrollContainerElement = scrollContainerRef.current;

    if (!scrollContainerElement) {
      return;
    }

    const handleScroll = () => {
      if (window.innerWidth < 768) {
        return;
      }

      const maxScroll =
        scrollContainerElement.scrollHeight -
        scrollContainerElement.clientHeight -
        (stickySideHeight === 0 ? 550 : stickySideHeight);

      if (scrollContainerElement.scrollTop > maxScroll) {
        scrollContainerElement.scrollTop = maxScroll + 10;
      }
    };

    scrollContainerElement.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainerElement.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef.current, stickySideHeight]);

  const getFormattedDate = (date: string) => {
    const reviewDate = new Date(date);
    const now = new Date();

    const diffInMinutes = differenceInMinutes(now, reviewDate);
    if (diffInMinutes < 60) {
      return {
        value: diffInMinutes,
        label: t("ds.minutes", { count: diffInMinutes }),
      };
    }

    const diffInHours = differenceInHours(now, reviewDate);
    if (diffInHours < 24) {
      return {
        value: diffInHours,
        label: t("ds.hours", { count: diffInHours }),
      };
    }

    const diffInDays = differenceInDays(now, reviewDate);
    if (diffInDays < 7) {
      return {
        value: diffInDays,
        label: t("ds.days", { count: diffInDays }),
      };
    }

    const diffInWeeks = differenceInWeeks(now, reviewDate);
    if (diffInWeeks < 4) {
      return {
        value: diffInWeeks,
        label: t("ds.weeks", { count: diffInWeeks }),
      };
    }

    const diffInMonths = differenceInMonths(now, reviewDate);
    if (diffInMonths < 12) {
      return {
        value: diffInMonths,
        label: t("ds.months", { count: diffInMonths }),
      };
    }

    const diffInYears = differenceInYears(now, reviewDate);
    return {
      value: diffInYears,
      label: t("ds.years", { count: diffInYears }),
    };
  };

  const openFullScreenCarousel = (startIndex: number) => {
    openModal(
      {
        content: (
          <FullScreenCarousel
            startIndex={startIndex}
            totalImagesCount={hotelDetail?.property.images?.length || 0}
            imageUrls={hotelDetail?.property?.images || []}
            hotelId={hotelDetail?._id || ""}
            hotelName={hotelDetail?.name || ""}
          />
        ),
        className:
          "w-full max-w-[900px] bg-black md:rounded-3xl h-[100dvh] md:h-[60dvh]",
      },
      { hideModalCloseBtn: true, isNew: true },
    );
    fireTagManagerEvent("open_details_section", {
      detail_section: ANALYTICS_DETAILS_SECTION.PHOTO,
      user_id: user?._id ?? null,
    });
  };

  const scrollToSection = (ref: string) => {
    const el = document.getElementById(ref);
    const parent = document.getElementById("detailPage");

    if (!el || !parent) return;

    const parentRect = parent.getBoundingClientRect();
    const childRect = el.getBoundingClientRect();

    const scrollTop =
      childRect.top - parentRect.top + parent.scrollTop - navbarHeight * 2;

    parent.scrollTo({ top: scrollTop, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full bg-white flex justify-center">
        <div className="px-4 w-full md:w-3/4 lg:w-2/3 2xl:w-1/2">
          <Skeleton count={3} className="mb-2" />
          <Skeleton height={200} className="mb-4" />
          <Skeleton count={3} className="mb-2" />
          <Skeleton width={150} />
        </div>
      </div>
    );
  }

  if (!hotelDetail) {
    return <NotFound />;
  }

  return (
    <div
      ref={scrollContainerRef}
      style={{
        paddingTop: navbarHeight,
      }}
      className="bg-white text-black flex items-center w-full flex-col overflow-y-auto h-screen"
      id="detailPage"
    >
      <div className="max-w-[1200px] w-full flex flex-col-reverse md:flex-col gap-6 bg-white md:pt-6">
        <div className="px-4">
          <div className="flex justify-between">
            <div>
              <button
                type="button"
                className="bg-transparent border-none relative touch-hitbox"
                onClick={() => {
                  const hotelElement = document.getElementById(hotelDetail._id);
                  if (hotelElement) {
                    const yOffset = -80;
                    const y =
                      hotelElement.getBoundingClientRect().top +
                      window.pageYOffset +
                      yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }}
              >
                <h2 className="text-xl font-medium text-left mb-0 text-black">
                  {hotelDetail.name}
                </h2>
              </button>
              <div className="flex flex-col gap-1 text-[#747474]">
                {!!hotelDetail.reviewData?.reviewScore &&
                  !!hotelDetail.reviewData?.reviewCount && (
                    <p className="text-sm font-medium text-left flex items-center gap-1">
                      <span>{hotelDetail.reviewData?.reviewScore}</span>
                      <IconStar className="size-2.5" />
                      <span>
                        {t(
                          `ds.rating_${
                            hotelDetail.reviewData?.reviewScore
                              .toString()
                              .split(".")[0]
                          }`,
                        )}
                      </span>
                      <span
                        className="hover:underline cursor-pointer"
                        onClick={() => scrollToSection("reviews")}
                      >
                        {`(${new Intl.NumberFormat(
                          searchParameters?.language ?? "en-US",
                        ).format(hotelDetail.reviewData?.reviewCount)} ${t(
                          "ds.review",
                          {
                            count: hotelDetail.reviewData?.reviewCount,
                          },
                        )})`}
                      </span>
                    </p>
                  )}
                {!(
                  !!hotelDetail.reviewData?.reviewScore &&
                  !!hotelDetail.reviewData?.reviewCount
                ) && (
                  <p className="text-sm font-medium text-left flex items-center gap-1">
                    {t("ds.no_reviews_yet")}
                  </p>
                )}
                {hotelDetail.stars && Number(hotelDetail.stars) !== 0 && (
                  <p className="text-sm font-medium text-left">
                    {`Hotel ${hotelDetail.stars} ${t("ds.star", {
                      count: Number(hotelDetail.stars),
                    })}`}
                  </p>
                )}
                <p className="text-sm font-medium text-left">
                  {`${!!hotelDetail.address ? hotelDetail.address : ""}${
                    !!hotelDetail.city ? ` ${hotelDetail.city}` : ""
                  }`}
                </p>
              </div>
            </div>
            {hotelDetail.isActive ? (
              <div className="md:flex gap-1 items-end justify-center hidden">
                <IconNew
                  icon="globe"
                  type="symbol"
                  isLucid
                  color="#747474"
                  size={18}
                />
                <a
                  className=" text-sm text-[#747474] underline"
                  href={hotelDetail.website}
                  target="_blank"
                  rel="noreferrer"
                >
                  {extractDomain(hotelDetail.website) ??
                    t("ds.official_website")}
                </a>
              </div>
            ) : null}
          </div>
        </div>
        {/* Images */}
        {hotelDetail.property.images.length > 0 && (
          <div className="md:px-4">
            <div className="relative md:rounded-xl overflow-hidden">
              <div className=" h-[400px] bg-white gap-2 hidden md:flex">
                <ImageWithFallback
                  src={hotelDetail.property.images[0].replace(
                    "square60",
                    "square600",
                  )}
                  alt={`${hotelDetail.name}`}
                  // style={{
                  //   aspectRatio: isLargeScreen ? "1 / 0.5" : "1 / 0.8",
                  //   objectFit: "cover",
                  // }}
                  className="w-1/2 object-cover"
                  onClick={() => {
                    openFullScreenCarousel(0);
                  }}
                />
                <div className="grid grid-cols-2 gap-2 w-1/2">
                  {hotelDetail.property.images
                    .map((url, index) => (
                      <div key={`hotel-img-${index}-${hotelDetail._id}`}>
                        <ImageWithFallback
                          src={url.replace("square60", "square600")}
                          alt={`${hotelDetail.name} - ${index + 1}`}
                          style={{
                            aspectRatio: isLargeScreen ? "1 / 0.5" : "1 / 0.8",
                          }}
                          className="w-full object-cover h-full"
                          onClick={() => {
                            openFullScreenCarousel(index);
                          }}
                        />
                      </div>
                    ))
                    .slice(1, 5)}
                </div>
              </div>

              <Carousel setApi={setCarouselApi} className="block md:hidden">
                <CarouselContent>
                  {hotelDetail.property.images
                    ?.map((url, index) => (
                      <CarouselItem
                        key={`hotel-img-${index}-${hotelDetail._id}`}
                        onClick={() => {
                          openFullScreenCarousel(index);
                        }}
                      >
                        <ImageWithFallback
                          src={url.replace("square60", "square600")}
                          alt={`${hotelDetail.name} - ${index + 1}`}
                          style={{
                            width: "100%",
                            aspectRatio: isLargeScreen ? "1 / 0.5" : "1 / 0.8",
                            objectFit: "cover",
                          }}
                        />
                      </CarouselItem>
                    ))
                    .slice(0, 8)}
                </CarouselContent>
                <CarouselPrevious className="left-4" />
                <CarouselNext className="right-4" />
              </Carousel>
              {md ? (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-2 xl:bottom-2 flex items-center gap-1">
                  {carouselSlidesCount > 0 &&
                    Array.from({ length: carouselSlidesCount }).map(
                      (_, index) => (
                        <div
                          key={index}
                          className={`size-2 rounded-full ${
                            carouselCurrentSlide === index
                              ? "bg-white"
                              : "bg-white/50"
                          }`}
                        />
                      ),
                    )}
                </div>
              ) : null}
              <button
                type="button"
                className="rounded-2xl flex items-center justify-center border-none box-border bg-[#F8F8F8] ring-4 ring-white ring-inset shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] gap-2 absolute bottom-4 right-4 text-xs font-medium px-4 py-3"
                onClick={() => {
                  openModal(
                    {
                      content: (
                        <div className="flex flex-col h-full">
                          <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12">
                            <h3 className="text-sm font-medium text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                              {`${t("ds.photos")} (${
                                hotelDetail.property?.images?.length
                              })`}
                            </h3>
                            <button
                              type="button"
                              onClick={closeModal}
                              className="bg-transparent border-none touch-hitbox relative"
                            >
                              <IconNew icon="close" className="size-5" />
                            </button>
                          </div>
                          <div className="overflow-y-scroll h-full px-4 py-3 grid grid-cols-2 gap-4">
                            {hotelDetail.property?.images.map((url, index) => (
                              <button
                                type="button"
                                key={`gallery-img-${index}-${hotelDetail._id}`}
                                className="bg-transparent border-none"
                                onClick={() => {
                                  openFullScreenCarousel(index);
                                }}
                              >
                                <ImageWithFallback
                                  src={url.replace("square60", "square600")}
                                  alt={`${hotelDetail.property.name} - ${
                                    index + 1
                                  }`}
                                  className="w-full object-cover rounded-lg"
                                  style={{
                                    aspectRatio: "1 / 0.8",
                                  }}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      ),
                    },
                    {
                      hideModalCloseBtn: true,
                      isNew: true,
                    },
                  );
                  fireTagManagerEvent("open_details_section", {
                    detail_section: ANALYTICS_DETAILS_SECTION.PHOTO,
                    user_id: user?._id ?? null,
                  });
                }}
              >
                <IconGallery className="size-4 text-black" />
                <span className="text-black">{t("ds.show_all")}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {md ? (
        <div className="mt-4 mb-8 px-4 w-full">
          {hotelDetail.isActive ? (
            <article className="rounded-2xl flex flex-col justify-center border-none box-border bg-[#F8F8F8] ring-4 ring-white ring-inset shadow-lg px-4 py-5 gap-4">
              <AvailabilityCard
                hotelDetail={hotelDetail}
                availability={availability}
                searchParameters={searchParameters}
                isSmallScreen={md}
                isExtraDiscountApplied={isExtraDiscountApplied}
                setSearchParameters={(sp) => setSearchParameters(sp)}
                isLoading={isAvailabilityLoading}
              />

              <div className="flex items-center gap-3.5">
                <img
                  src={imageBadgePlus}
                  role="presentation"
                  className="size-14 shrink-0"
                  alt=""
                />
                <div className="flex flex-col">
                  <h3 className="text-sm font-medium mb-0">
                    {t("ds.hotel_details_badge_title")}
                  </h3>
                  <p className="text-xs font-medium text-[#747474]">
                    {t("ds.hotel_details_badge_subtitle")}
                  </p>
                </div>
              </div>
              <hr className="h-px bg-gray-500 w-full border-0 my-0" />
              <ul className="list-none flex flex-col w-full px-4 gap-4 mb-0">
                {highlightedAdvantage &&
                  highlightedAdvantage !== "lowest_price" && (
                    <li className="flex gap-6 items-center">
                      {HighlightedAdvantageIcon && (
                        <HighlightedAdvantageIcon className="size-5" />
                      )}
                      <div className="flex flex-col">
                        <h3 className="text-sm font-medium mb-0">
                          {t(highlightedAdvantageLabel)}{" "}
                          {resellableAdvantages.includes(highlightedAdvantage)
                            ? "Members-only"
                            : ""}
                        </h3>
                        <p className="text-xs font-medium text-[#747474]">
                          {t("ds.included_by_booking_on_the_official_website")}
                        </p>
                      </div>
                    </li>
                  )}
                {Array.from({ length: advantagesIcons.length }).map(
                  (_, index) => {
                    const Icon = advantagesIcons[index];
                    return (
                      <li className="flex gap-6 items-center">
                        <Icon className="size-5" />
                        <div className="flex flex-col">
                          <h3 className="text-sm font-medium mb-0">
                            {t(`ds.hotel_details_advantages_titles.${index}`)}
                          </h3>
                          <p className="text-xs font-medium text-[#747474]">
                            {t(
                              `ds.hotel_details_advantages_subtitles.${index}`,
                            )}
                          </p>
                        </div>
                      </li>
                    );
                  },
                )}
              </ul>
            </article>
          ) : (
            <AvailabilityCard
              hotelDetail={hotelDetail}
              availability={availability}
              searchParameters={searchParameters}
              isSmallScreen={md}
              isExtraDiscountApplied={isExtraDiscountApplied}
              setSearchParameters={(sp) => setSearchParameters(sp)}
              isLoading={isAvailabilityLoading}
            />
          )}
        </div>
      ) : null}

      <div className="sticky top-0 w-full flex flex-col items-center justify-center pt-6 bg-white z-[19]">
        <div className="px-6 max-w-[1200px] w-full flex bg-white relative gap-6">
          {goSections.map((section, index) => (
            <div key={`section-${index}`} className="flex flex-col w-fit">
              <p
                className={`text-base font-medium px-3 cursor-pointer ${
                  activeSection === section.ref ? "text-black" : "text-gray-400"
                }`}
                onClick={() => scrollToSection(section.ref)}
              >
                {section.name}
              </p>
              <div
                className={`absolute -bottom-5 h-1 w-fit ${
                  activeSection === section.ref ? "bg-black" : "bg-white"
                }`}
              >
                <p className="px-3 h-0 pointer-events-none opacity-0">
                  {section.name}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div
          className=""
          style={{
            width: "100%",
            marginTop: 20,
          }}
        ></div>
      </div>
      <div className="py-4 pb-0 max-w-[1200px] w-full">
        <div className="flex flex-col">
          <div
            ref={stickySideRef}
            className="hidden md:sticky top-2 self-end w-full md:w-1/2 lg:w-1/3 justify-end md:z-[999] pl-6 px-4 md:flex flex-col gap-4 "
          >
            {/* Name and stars */}
            {hotelDetail.isActive ? (
              <article className="rounded-2xl flex flex-col justify-center border-none box-border bg-[#F8F8F8] ring-4 ring-white ring-inset shadow-lg px-4 py-5 gap-4">
                <div className="hidden md:block">
                  <AvailabilityCard
                    hotelDetail={hotelDetail}
                    availability={availability}
                    searchParameters={searchParameters}
                    isSmallScreen={md}
                    isExtraDiscountApplied={isExtraDiscountApplied}
                    setSearchParameters={(sp) => setSearchParameters(sp)}
                    isLoading={isAvailabilityLoading}
                  />
                </div>
                <div className="flex px-2 items-center gap-3.5">
                  <img
                    src={imageBadgePlus}
                    role="presentation"
                    className="size-8 shrink-0"
                    alt=""
                  />
                  <div className="flex flex-col">
                    <h3 className="text-sm font-medium mb-0">
                      {t("ds.hotel_details_badge_title")}
                    </h3>
                    <p className="text-xs font-medium text-[#747474]">
                      {t("ds.hotel_details_badge_subtitle")}
                    </p>
                  </div>
                </div>
                <hr className="h-px bg-gray-500 w-full border-0 my-0" />
                <ul className="list-none flex flex-col w-full px-4 gap-4 mb-0">
                  {highlightedAdvantage &&
                    highlightedAdvantage !== "lowest_price" && (
                      <li className="flex gap-6 items-center">
                        {HighlightedAdvantageIcon && (
                          <HighlightedAdvantageIcon className="size-5" />
                        )}
                        <div className="flex flex-col">
                          <h3 className="text-sm font-medium mb-0">
                            {t(highlightedAdvantageLabel)}{" "}
                            {resellableAdvantages.includes(highlightedAdvantage)
                              ? "Members-only"
                              : ""}
                          </h3>
                          <p className="text-xs font-medium text-[#747474]">
                            {t(
                              "ds.included_by_booking_on_the_official_website",
                            )}
                          </p>
                        </div>
                      </li>
                    )}

                  {Array.from({ length: advantagesIcons.length }).map(
                    (_, index) => {
                      const Icon = advantagesIcons[index];
                      return (
                        <li className="flex gap-6 items-center">
                          <Icon className="size-5" />
                          <div className="flex flex-col">
                            <h3 className="text-sm font-medium mb-0">
                              {t(`ds.hotel_details_advantages_titles.${index}`)}
                            </h3>
                            <p className="text-xs font-medium text-[#747474]">
                              {t(
                                `ds.hotel_details_advantages_subtitles.${index}`,
                              )}
                            </p>
                          </div>
                        </li>
                      );
                    },
                  )}
                </ul>
              </article>
            ) : (
              <AvailabilityCard
                hotelDetail={hotelDetail}
                availability={availability}
                searchParameters={searchParameters}
                isSmallScreen={md}
                isExtraDiscountApplied={isExtraDiscountApplied}
                setSearchParameters={(sp) => setSearchParameters(sp)}
                isLoading={isAvailabilityLoading}
              />
            )}
          </div>
          {/* Menu go Section */}
          <div
            className="w-full md:w-1/2 lg:w-2/3"
            style={
              md
                ? {}
                : {
                    transform: `translateY(-${stickySideHeight}px)`,
                  }
            }
          >
            <div className="px-4 lg:px-0">
              {/* Description */}
              <div className="lg:px-4">
                <h3 className="text-base font-medium mb-0">
                  {t("ds.description")}
                </h3>
                <HotelDescription
                  html={
                    hotelDetail.property?.description.find(
                      (i) => i.languagecode === i18n.language,
                    )?.description ??
                    hotelDetail.property?.description.find(
                      (i) => i.languagecode === LANG.en,
                    )?.description ??
                    ""
                  }
                />
              </div>
              <div className="lg:px-4" id="info">
                <div className="horizontal-divider"></div>
              </div>
              {/* Arrivals / Departures */}
              <div className="lg:px-4">
                <h3 className="text-base font-medium mb-0">
                  {t("ds.arrivals_departures_title_detail")}
                </h3>
              </div>
              <div className="lg:px-4">
                <ul className="flex flex-col list-none pl-0 mt-4">
                  <li className="d-flex align-items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <IconNew icon="download" isLucid size={20} />
                      <p className="text-sm font-medium">{t("ds.arrival")}</p>
                    </div>
                    <p className="text-sm font-medium">
                      {t("ds.from")} {hotelDetail.property?.checkin?.fromTime}
                    </p>
                  </li>
                  <div className="horizontal-divider"></div>
                  <li className="d-flex align-items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <IconNew icon="upload" isLucid size={20} />
                      <p className="text-sm font-medium">{t("ds.departure")}</p>
                    </div>
                    <p className="text-sm font-medium">
                      {t("ds.until")}{" "}
                      {hotelDetail.property?.checkout?.untilTime}
                    </p>
                  </li>
                  <div className="horizontal-divider"></div>
                  {/* <li className="d-flex align-items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <IconNew icon="paw_print" isLucid size={20} />
                                      <p className="text-sm font-medium">
                                        {t("ds.animals")}
                                      </p>
                                    </div>
                                    <p className="text-sm font-medium">
                                      {t("ds.adminted_on_request")}
                                    </p>
                                  </li>
                                  <div className="horizontal-divider"></div>
                                  <li className="d-flex align-items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <IconNew icon="baby" isLucid size={20} />
                                      <p className="text-sm font-medium">
                                        {t("ds.babies")}
                                      </p>
                                    </div>
                                    <p className="text-sm font-medium">
                                      {t("ds.admitted")}
                                    </p>
                                  </li>*/}
                </ul>
              </div>
              {/* <div className="horizontal-divider"></div> */}
              {/* Facilities */}
              <div className="lg:px-4">
                <h3 className="text-base font-medium mb-5">
                  {t("ds.best_facilities")}
                </h3>
              </div>
              <div className="lg:px-4">
                <div className="flex flex-wrap gap-2 justify-center">
                  {facilitiesToShow?.map((f, index) => (
                    <div
                      key={`facility-${index}-${hotelDetail._id}`}
                      className="inline-flex items-center px-4 py-2 rounded-pill border"
                    >
                      <p className="text-sm font-medium m-0 text-center">
                        {t(`facilities.${f.instances[0].title}`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full flex justify-center mt-6">
                <button
                  type="button"
                  className="bg-[#F3F3F3] text-sm font-medium rounded-xl px-3.5 h-[2.375rem] flex items-center justify-center border-none text-black"
                  onClick={() => {
                    openModal(
                      {
                        content: (
                          <div className="flex flex-col h-full">
                            <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12 shrink-0">
                              <h3 className="text-sm font-medium text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                                {t("ds.facilities")}
                              </h3>
                              <button
                                type="button"
                                onClick={closeModal}
                                className="bg-transparent border-none touch-hitbox relative"
                              >
                                <IconNew icon="close" className="size-5" />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 justify-center px-4 py-3 overflow-y-auto">
                              {hotelDetail?.property?.facilities?.map(
                                (f, index) => (
                                  <div
                                    key={`facility-${index}-${hotelDetail._id}`}
                                    className="flex items-center px-4 py-2 rounded-pill border shrink h-fit"
                                  >
                                    <p className="text-sm font-medium m-0 text-center">
                                      {t(`facilities.${f.instances[0].title}`)}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        ),
                      },
                      {
                        hideModalCloseBtn: true,
                        isNew: true,
                      },
                    );
                    fireTagManagerEvent("open_details_section", {
                      detail_section: ANALYTICS_DETAILS_SECTION.AMENITIES,
                      user_id: user?._id ?? null,
                    });
                  }}
                >
                  {t("ds.read_more_facilities")}
                </button>
              </div>
              <div className="lg:px-4" id="reviews">
                <div className="horizontal-divider"></div>
              </div>
              {/* Review title */}
              <div className="lg:px-4 flex flex-col gap-1">
                <h3 className="fs-body medium mb-0">
                  {t("ds.guests_reviews")}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    openModal({
                      content: (
                        <div className="flex flex-col gap-4 px-6 pb-10 lg:pt-10">
                          <h4 className="text-base font-medium">
                            {t("ds.reviews_source_modal_title")}
                          </h4>
                          <p className="flex flex-col text-sm font-medium text-[#747474]">
                            <div>
                              <span>
                                {`${t(
                                  "ds.reviews_source_modal_description.0",
                                )}`}
                              </span>
                              <span className="font-bold ml-1">
                                {`Booking.`}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-[#747474]">
                              {`${t("ds.reviews_source_modal_description.1")}`}
                            </span>
                          </p>
                        </div>
                      ),
                      className:
                        "h-fit self-end rounded-t-[1.25rem] lg:h-auto lg:self-center lg:rounded-3xl",
                      native: isSmallScreen,
                    });
                  }}
                  className="flex items-center gap-1 text-xs font-medium text-[#747474] bg-transparent border-none"
                >
                  <span>{t("ds.reviews_source")}</span>
                  <IconInfo className="size-3 shrink-0" />
                </button>
              </div>
              {!!hotelDetail.reviewData?.reviewScore &&
                !!hotelDetail.reviewData?.reviewCount && (
                  <div className="flex flex-col items-center gap-1 mt-4 lg:px-4">
                    <div className="flex items-center gap-1 bg-black px-3 rounded-full">
                      <p
                        className="py-1.5 px-1 medium review-score text-white"
                        style={{ marginLeft: "8px" }}
                      >
                        {hotelDetail.reviewData.reviewScore}
                      </p>
                    </div>
                    <p className="text-base font-medium review-summary">
                      {t(
                        `ds.rating_${
                          hotelDetail.reviewData?.reviewScore
                            .toString()
                            .split(".")[0]
                        }`,
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      {`${new Intl.NumberFormat(
                        searchParameters?.language ?? "en-US",
                      ).format(hotelDetail.reviewData?.reviewCount)} ${t(
                        "ds.total_reviews",
                        {
                          count: hotelDetail.reviewData?.reviewCount,
                        },
                      )}`}
                    </p>
                  </div>
                )}
              {!(
                !!hotelDetail.reviewData?.reviewScore &&
                !!hotelDetail.reviewData?.reviewCount
              ) && (
                <div className="w-full flex justify-center">
                  <p className="text-sm font-medium text-[#747474] opacity-70 bg-gray-100 px-4 py-2 rounded-full text-left flex items-center gap-1">
                    {t("ds.no_reviews_yet")}
                  </p>
                </div>
              )}
              {/* Score Percentage */}
              {/* Appreciation Services */}
              {/* Reviews */}
              {reviewsToShow && reviewsToShow.length > 0 ? (
                <>
                  <div className="lg:px-4">
                    <h3 className="text-base font-medium mt-8 mb-4">
                      {t("ds.appreciation_services")}
                    </h3>
                  </div>
                  <ul className="flex overflow-x-auto snap-x px-4 gap-4 w-screen lg:w-full -translate-x-4 lg:translate-x-0 list-none pb-2">
                    {reviewsToShow.map((review, index) => (
                      <li
                        key={`review-${index}-${review.title}-${hotelDetail._id}`}
                        className="p-3 rounded-2xl shadow flex flex-col min-w-[13.25rem] gap-4 border border-[#E8E8E8]"
                      >
                        <header className="flex items-center gap-2">
                          <div className="rounded-full flex items-center justify-center bg-[#F8F8F8] px-4 py-1">
                            {Math.min(review.average_score * 2.5, 10).toFixed(
                              0,
                            )}
                          </div>
                          <div className="flex flex-col">
                            <h4 className="text-sm font-medium mb-0">
                              {review.author.name}
                            </h4>
                            <p className="text-xs font-medium text-[#747474]">
                              {t(
                                `ds.country_codes_nations.${review.author.countrycode.toUpperCase()}`,
                              )}
                            </p>
                          </div>
                        </header>
                        <main>
                          <p
                            className="text-xs font-medium"
                            style={{
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 5,
                              WebkitBoxOrient: "vertical",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {review.pros_translated || review.pros}
                          </p>
                        </main>
                        <footer>
                          <p className="text-xs font-medium text-[#A2A2A2]">
                            {`${
                              getFormattedDate(review.date).value
                            } ${getFormattedDate(
                              review.date,
                            ).label.toLowerCase()} ${t("ds.ago")}`}
                          </p>
                        </footer>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-center mt-5">
                    <button
                      type="button"
                      className="bg-[#F3F3F3] text-sm font-medium rounded-xl px-3.5 h-[2.375rem] flex items-center justify-center border-none text-black"
                      disabled={!reviewsToShow || reviewsToShow.length === 0}
                      style={{
                        opacity:
                          !reviewsToShow || reviewsToShow.length === 0
                            ? 0.5
                            : 1,
                      }}
                      onClick={() => {
                        openModal(
                          {
                            content: (
                              <div className="flex flex-col h-full">
                                <div className="relative flex justify-end px-3 items-center box-border border-bottom h-12">
                                  <h3 className="text-sm font-medium text-center mb-0 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                                    {t("ds.guests_reviews")}
                                  </h3>
                                  <button
                                    type="button"
                                    onClick={closeModal}
                                    className="bg-transparent border-none touch-hitbox relative"
                                  >
                                    <IconNew icon="close" className="size-5" />
                                  </button>
                                </div>
                                <div className="flex flex-col overflow-y-auto h-full px-4 py-3">
                                  <Reviews hotel={hotelDetail} hideHeader />
                                </div>
                              </div>
                            ),
                          },
                          { hideModalCloseBtn: true, isNew: true },
                        );
                        fireTagManagerEvent("open_details_section", {
                          detail_section: ANALYTICS_DETAILS_SECTION.REVIEWS,
                          user_id: user?._id ?? null,
                        });
                      }}
                    >
                      {t("ds.read_guests_reviews")}
                    </button>
                  </div>
                </>
              ) : null}

              <div className="lg:px-4">
                <div className="horizontal-divider" id="map"></div>
              </div>
              {/* Map */}
              <div className="lg:px-4">
                <h3 className="text-base font-medium mb-0">
                  {t("ds.address_position")}
                </h3>
              </div>
              <div className="relative lg:px-4">
                <div
                  className="mt-8 rounded-2xl overflow-hidden relative"
                  style={{
                    height: "358px",
                    width: "100%",
                  }}
                >
                  <DetailMap hotel={hotelDetail} />
                </div>
                {!isLargeScreen && (
                  <button
                    type="button"
                    className="rounded-2xl px-4 py-3 flex items-center justify-center border-none box-border bg-[#F8F8F8] shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-medium text-black"
                    onClick={() => {
                      openModal(
                        {
                          content: (
                            <>
                              <DetailMap hotel={hotelDetail} />
                              <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-2xl size-12 flex items-center justify-center border-none box-border bg-[#F8F8F8] ring-4 ring-white ring-inset shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] absolute top-4 right-4 z-20"
                              >
                                <IconNew icon="close" size={25} />
                              </button>
                            </>
                          ),
                          className:
                            "rounded-t-3xl self-end h-[calc(100dvh-1rem)] lg:h-auto lg:self-center lg:rounded-3xl",
                        },
                        { hideModalCloseBtn: true },
                      );
                      fireTagManagerEvent("open_details_section", {
                        detail_section: ANALYTICS_DETAILS_SECTION.MAP,
                        user_id: user?._id ?? null,
                      });
                    }}
                  >
                    {t("ds.show_on_map")}
                  </button>
                )}
              </div>
              {/* <span
                                  className="px-2 py-1.5 rounded-full w-fit mt-4 bg-[#E8FAE7] text-xs font-medium flex items-center justify-center"
                                >
                                  Posizione eccellente
                                </span> */}
              {/* Address */}
              <div className="lg:px-4">
                <p className="text-sm font-medium mt-2">
                  {`${!!hotelDetail.address ? hotelDetail.address : ""}${
                    !!hotelDetail.city ? ` ${hotelDetail.city}` : ""
                  }`}
                </p>
              </div>
              <div className="lg:px-4">
                <div className="flex items-center gap-4 mt-4">
                  <button
                    type="button"
                    className="flex items-center gap-1 bg-transparent border-none text-[#A2A2A2]"
                    onClick={() => {
                      copyToClipboard(
                        `${!!hotelDetail.address ? hotelDetail.address : ""}${
                          !!hotelDetail.city ? ` ${hotelDetail.city}` : ""
                        }`,
                      );
                      toast.success(t("ds.address_copied"));
                    }}
                  >
                    <IconCopy className="size-4" />
                    <p className="text-xs font-medium">{t("ds.copy")}</p>
                  </button>
                  <a
                    className="flex items-center gap-1 text-[#A2A2A2] no-underline"
                    href={`https://www.google.com/maps/search/?api=1&query=${hotelDetail.mongoDbLocation.coordinates[1]},${hotelDetail.mongoDbLocation.coordinates[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconOpen className="size-3" />
                    <p className="text-xs font-medium">
                      {t("ds.open_in_maps")}
                    </p>
                  </a>
                </div>
              </div>

              {availability && availability.availabilityInfo.available && (
                <div className={!isExtraDiscountApplied ? "h-24" : "h-28"} />
              )}
              {/* Prices */}
            </div>
          </div>
        </div>
      </div>
      {hotelDetail.isActive ? (
        <div className="md:hidden fixed bottom-0 md:static w-full left-0 z-[999]">
          <AvailabilityCardMobile
            hotelDetail={hotelDetail}
            availability={availability}
            searchParameters={searchParameters}
            isSmallScreen={md}
            isExtraDiscountApplied={isExtraDiscountApplied}
          />
        </div>
      ) : null}
    </div>
  );
};

export default DetailPage;
